import React, { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { projects } from '../data/portfolio';

// Filter tabs
const FILTERS = [
  { label: 'All',        value: null },
  { label: 'AI / ML',   value: 'ai' },
  { label: 'Full Stack', value: 'fullstack' },
  { label: 'Mobile',    value: 'mobile' },
  { label: 'Backend',   value: 'backend' },
  { label: 'Cloud',     value: 'cloud' },
];

// Single project card
const ProjectCard = ({ project, index, onOpen }) => {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.08 });
  const [hovered, setHovered] = useState(false);

  return (
    <article
      ref={ref}
      className="project-card cursor-pointer group"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.6s ease ${index * 80}ms, transform 0.6s ease ${index * 80}ms`,
      }}
      onClick={() => onOpen(project.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(project.id)}
      aria-label={`Open ${project.title} project details`}
    >
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: '220px' }}>
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-500"
          style={{
            transform: hovered ? 'scale(1.04)' : 'scale(1)',
          }}
          loading="lazy"
        />
        {/* Overlay */}
        <div
          className="absolute inset-0 transition-opacity duration-300"
          style={{
            background: 'linear-gradient(to top, rgba(10,10,10,0.7) 0%, transparent 60%)',
          }}
        />

        {/* Category pills on image */}
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {project.categoryLabels.slice(0, 2).map((cat) => (
            <span
              key={cat}
              className="font-mono text-xs px-2 py-1"
              style={{
                background: 'rgba(10,10,10,0.75)',
                color: 'var(--color-text2)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              {cat}
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            {/* Index */}
            <div
              className="font-mono text-xs mb-2"
              style={{ color: 'var(--color-accent)' }}
            >
              {String(index + 1).padStart(2, '0')}
            </div>
            <h3
              className="font-bold text-xl md:text-2xl leading-tight"
              style={{ color: 'var(--color-text)' }}
            >
              {project.title}
            </h3>
          </div>

          {/* Arrow */}
          <span
            className="card-arrow text-xl mt-1 flex-shrink-0"
            style={{ color: hovered ? 'var(--color-accent)' : 'var(--color-text2)' }}
            aria-hidden="true"
          >
            ↗
          </span>
        </div>

        <p
          className="text-sm leading-relaxed mb-4"
          style={{ color: 'var(--color-text2)' }}
        >
          {project.description}
        </p>

        {/* Tech stack */}
        <div className="flex flex-wrap gap-2">
          {project.technologies.map((tech) => (
            <span
              key={tech}
              className="font-mono text-xs px-2 py-1"
              style={{
                color: 'var(--color-text2)',
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
              }}
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
};

const Projects = ({ activeFilter, onOpen }) => {
  const { ref: headRef, isVisible: headVisible } = useScrollReveal({ threshold: 0.2 });
  const [localFilter, setLocalFilter] = useState(null);

  // activeFilter from command interface takes precedence
  const effectiveFilter = activeFilter ?? localFilter;

  const filtered = effectiveFilter
    ? projects.filter((p) => p.categories.includes(effectiveFilter))
    : projects;

  return (
    <section
      id="projects"
      className="py-24 md:py-36"
      style={{ backgroundColor: 'var(--color-bg)', borderTop: '1px solid var(--color-border)' }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div ref={headRef} className="mb-12 md:mb-16">
          <div
            className="section-label mb-6"
            style={{
              opacity: headVisible ? 1 : 0,
              transition: 'opacity 0.5s ease',
            }}
          >
            02 / PROJECTS
          </div>
          <h2
            className="font-bold leading-none tracking-tight mb-2"
            style={{
              fontSize: 'clamp(2.2rem, 6vw, 5rem)',
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
            }}
          >
            THINGS
          </h2>
          <h2
            className="font-bold leading-none tracking-tight"
            style={{
              fontSize: 'clamp(2.2rem, 6vw, 5rem)',
              color: 'var(--color-text2)',
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
            }}
          >
            I'VE BUILT.
          </h2>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-10">
          {FILTERS.map((f) => {
            const isActive = effectiveFilter === f.value;
            return (
              <button
                key={f.label}
                onClick={() => setLocalFilter(f.value)}
                className="font-mono text-xs px-4 py-2 transition-all duration-200"
                style={{
                  border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  color: isActive ? 'var(--color-accent)' : 'var(--color-text2)',
                  background: isActive ? 'rgba(183,255,60,0.06)' : 'transparent',
                }}
              >
                {f.label}
                {isActive && f.value && (
                  <span className="ml-2">
                    ({filtered.length})
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Project grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px" style={{ background: 'var(--color-border)' }}>
          {filtered.map((project, i) => (
            <ProjectCard
              key={project.id}
              project={project}
              index={i}
              onOpen={onOpen}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="font-mono text-sm" style={{ color: 'var(--color-text2)' }}>
              No projects in this category yet.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
